export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://server.onechatting.com').replace(/\/$/, '');

export const navSections = [
  {
    title: 'Getting Started',
    items: [
      { label: 'Introduction', path: '/' },
      { label: 'Authentication', path: '/authentication' },
    ],
  },
  {
    title: 'Messages',
    items: [
      { label: 'Chat List', path: '/messages/chat-list' },
      { label: 'Chat History', path: '/messages/chat-history' },
      { label: 'Mark as Read', path: '/messages/mark-as-read' },
      { label: 'Send Text', path: '/messages/send-text' },
      { label: 'Send Image', path: '/messages/send-image' },
      { label: 'Send Video', path: '/messages/send-video' },
      { label: 'Send Document', path: '/messages/send-document' },
      { label: 'Send Audio', path: '/messages/send-audio' },
      { label: 'Send Template', path: '/messages/send-template' },
    ],
  },
  {
    title: 'Templates',
    items: [
      { label: 'Template List', path: '/templates/list' },
      { label: 'Template Details', path: '/templates/details' },
      { label: 'Create Template', path: '/templates/create' },
      { label: 'Edit Template', path: '/templates/edit' },
    ],
  },
  {
    title: 'Contacts',
    items: [
      { label: 'Bulk Upsert', path: '/contacts/bulk-upsert' },
      { label: 'Bulk Upsert Status', path: '/contacts/bulk-upsert-status' },
    ],
  },
  {
    title: 'Real-time',
    items: [
      { label: 'WebSocket Overview', path: '/websocket' },
      { label: 'Socket Events', path: '/websocket/events' },
    ],
  },
  {
    title: 'Developer Access',
    items: [
      { label: 'Access Info', path: '/developer-access/access-info' },
      { label: 'Update Access', path: '/developer-access/update-access' },
      { label: 'Project Token', path: '/developer-access/project-token' },
      { label: 'User Token', path: '/developer-access/user-token' },
    ],
  },
];

export const socketEvents = [
  {
    name: 'auth',
    direction: 'client → server',
    description: 'Authenticate the socket connection after connect. For developer integrations, use auth_type "developer" with your developer token.',
    payload: `{
  "token": "<developer_token>",
  "auth_type": "developer"
}`,
    notes: [
      'Use a User Token for WebSocket authentication (available from Developer Access).',
      'Project Tokens are not supported for WebSocket connections.',
    ],
  },
  {
    name: 'auth_status',
    direction: 'server → client',
    description: 'Authentication result. Connection is closed if false.',
    payload: `true | false`,
  },
  {
    name: 'auth_profile',
    direction: 'server → client',
    description: 'Sent after successful developer authentication with session context.',
    payload: `{
  "auth_type": "developer",
  "project_id": "6975c11a1f13c30dc18f1a30",
  "username": "abc123"
}`,
  },
  {
    name: 'chat',
    direction: 'server → client',
    description: 'New or updated message for a contact. Use project_id to scope events when integrating multiple projects.',
    payload: `{
  "project_id": "6975c11a1f13c30dc18f1a30",
  "contact": {
    "number": "919876543210",
    "name": "John Doe"
  },
  "message": {
    "message_id": "abc123unique30charstringhere12",
    "wamid": "wamid.HBgM...",
    "create_date": "2026-06-20 14:30:00",
    "type": "in",
    "message_type": "text",
    "message": "Hello!",
    "is_template": false,
    "is_forwarded": false,
    "is_reply": false,
    "status": "delivered",
    "id": 12345,
    "is_read": false,
    "send_by": {
      "username": "agent1",
      "name": "Agent Name",
      "mobile": "919999999999",
      "email": "agent@example.com",
      "status": true
    }
  }
}`,
    notes: [
      'type: "in" (received) or "out" (sent).',
      'message_type: text | image | video | audio | document | location | template.',
      'For templates, message contains the rendered body text when available; template and component are always included.',
      'AUTHENTICATION templates: message is built from the OTP code in component (e.g. "123456 is your verification code.").',
      'Template JSON header media URLs are signed B2 links when returned from the API.',
      'Media messages include media_url and media_name.',
    ],
  },
  {
    name: 'message_status',
    direction: 'server → client',
    description: 'Delivery status update for an outgoing message (sent, delivered, read, failed).',
    payload: `{
  "message_id": "abc123unique30charstringhere12",
  "last_id": 12345,
  "project_id": "6975c11a1f13c30dc18f1a30",
  "changes": "delivered",
  "failed_reason": "optional when changes is failed"
}`,
    notes: [
      'changes: sent | delivered | read | failed.',
      'Match updates using message_id or the id field from the message object.',
    ],
  },
  {
    name: 'chat_assigned',
    direction: 'server → client',
    description: 'Chat assignment changed for a contact number.',
    payload: `{
  "assigning": {
    "assigned": true,
    "assigned_to_me": false,
    "assigned_user": {
      "name": "Agent Name",
      "mobile": "919999999999",
      "email": "agent@example.com",
      "status": true,
      "username": "agent1"
    },
    "users": [
      {
        "username": "owner1",
        "name": "Owner",
        "mobile": "918888888888",
        "email": "owner@example.com",
        "type": "admin",
        "is_me": false
      }
    ]
  }
}`,
    notes: [
      'When assigned is false, assigned_user is omitted.',
      'users contains team members available for the conversation.',
    ],
  },
  {
    name: 'total_unread_count',
    direction: 'server → client',
    description: 'Total unread incoming messages for the project.',
    payload: `{
  "count": 12,
  "project_id": "6975c11a1f13c30dc18f1a30"
}`,
  },
  {
    name: 'case_status',
    direction: 'server → client',
    description: 'Open case count updated for a contact number.',
    payload: `{
  "number": "919876543210",
  "case_open_count": 2
}`,
  },
];

export const endpoints = {
  'chat-list': {
    title: 'Chat List',
    method: 'GET',
    path: '/developer/message/chat-list',
    auth: 'message',
    description: 'Returns paginated conversations for the authenticated project, with last message preview, unread count, and open case count per contact.',
    headers: [{ name: 'token', required: true, description: 'User Token or Project Token' }],
    query: [
      { name: 'page_no', type: 'number', required: false, description: 'Page number (default: 1)' },
      { name: 'limit', type: 'number', required: false, description: 'Items per page, max 100 (default: 100)' },
      { name: 'search', type: 'string', required: false, description: 'Search by number, message, contact name, email, etc.' },
    ],
    response: `{
  "data": [
    {
      "contact": { "number": "919876543210", "name": "John", "is_favorite": false },
      "case_open_count": 1,
      "last_message": {
        "id": 12345,
        "wamid": "wamid.HBgM...",
        "create_date": "2026-06-20 14:30:00",
        "type": "in",
        "message_type": "text",
        "message": "Hello",
        "status": "delivered",
        "unique_id": "abc123..."
      },
      "unread_count": 3
    }
  ],
  "count": 1,
  "pagination": {
    "page_no": 1,
    "limit": 100,
    "total": 50,
    "total_pages": 1,
    "has_more": false
  }
}`,
  },
  'chat-history': {
    title: 'Chat History',
    method: 'GET',
    path: '/developer/message/chat-history',
    auth: 'message',
    description: 'Fetch message history for a specific contact. Supports cursor pagination via last_id. Template messages include template JSON (with B2 media URLs), component parameters, and a rendered message field.',
    headers: [{ name: 'token', required: true, description: 'User Token' }],
    query: [
      { name: 'number', type: 'string', required: true, description: 'Contact WhatsApp number' },
      { name: 'last_id', type: 'number', required: false, description: 'Cursor — pass last message id from previous page (0 for first page)' },
      { name: 'limit', type: 'number', required: false, description: 'Max 100 (default: 100)' },
    ],
    response: `{
  "data": [
    {
      "message_id": "abc123...",
      "type": "out",
      "message_type": "template",
      "message": "123456 is your verification code. For your security, do not share this code.",
      "is_template": true,
      "template": { "category": "AUTHENTICATION", "components": [ /* ... */ ] },
      "component": [{ "type": "body", "parameters": [{ "type": "text", "text": "123456" }] }],
      "status": "sent"
    }
  ],
  "count": 20,
  "last_id": 12300,
  "assigning": { /* assignment object */ }
}`,
  },
  'mark-as-read': {
    title: 'Mark as Read',
    method: 'POST',
    path: '/developer/message/mark-as-read',
    auth: 'message',
    description: 'Mark all incoming messages from a contact as read.',
    headers: [{ name: 'token', required: true, description: 'User Token' }],
    body: [{ name: 'number', type: 'string', required: true, description: 'Contact WhatsApp number' }],
    response: `{ "error": false }`,
  },
  'send-text': {
    title: 'Send Text Message',
    method: 'POST',
    path: '/developer/message/send-text-message',
    auth: 'send',
    description: 'Send a text message to a WhatsApp number.',
    headers: [{ name: 'token', required: true, description: 'User Token or Project Token' }],
    body: [
      { name: 'number', type: 'string', required: true, description: 'Recipient WhatsApp number' },
      { name: 'message', type: 'string', required: true, description: 'Text body' },
      { name: 'is_reply', type: 'boolean', required: false, description: 'Whether this is a reply' },
      { name: 'reply_wamid', type: 'string', required: false, description: 'Required when is_reply is true' },
    ],
    response: `{
  "wamid": "wamid.HBgM...",
  "message_id": "abc123...",
  "message": "Hello!",
  "create_date": "2026-06-20 14:30:00",
  "type": "out",
  "message_type": "text",
  "status": "pending",
  "id": 12346,
  "send_by": { "username": "...", "name": "...", "mobile": "...", "email": "...", "status": true }
}`,
  },
  'send-image': {
    title: 'Send Image Message',
    method: 'POST',
    path: '/developer/message/send-image-message',
    auth: 'send',
    description: 'Send an image with optional caption.',
    body: [
      { name: 'number', type: 'string', required: true },
      { name: 'image_link', type: 'string', required: true, description: 'Public URL of the image' },
      { name: 'message', type: 'string', required: false, description: 'Caption' },
      { name: 'is_reply', type: 'boolean', required: false },
      { name: 'reply_wamid', type: 'string', required: false },
    ],
  },
  'send-video': {
    title: 'Send Video Message',
    method: 'POST',
    path: '/developer/message/send-video-message',
    auth: 'send',
    body: [
      { name: 'number', type: 'string', required: true },
      { name: 'video_link', type: 'string', required: true },
      { name: 'message', type: 'string', required: false, description: 'Caption' },
      { name: 'is_reply', type: 'boolean', required: false },
      { name: 'reply_wamid', type: 'string', required: false },
    ],
  },
  'send-document': {
    title: 'Send Document Message',
    method: 'POST',
    path: '/developer/message/send-document-message',
    auth: 'send',
    body: [
      { name: 'number', type: 'string', required: true },
      { name: 'document_link', type: 'string', required: true },
      { name: 'document_name', type: 'string', required: false, description: 'Display filename (default: Document)' },
      { name: 'message', type: 'string', required: false, description: 'Caption' },
      { name: 'is_reply', type: 'boolean', required: false },
      { name: 'reply_wamid', type: 'string', required: false },
    ],
  },
  'send-audio': {
    title: 'Send Audio Message',
    method: 'POST',
    path: '/developer/message/send-audio-message',
    auth: 'send',
    body: [
      { name: 'number', type: 'string', required: true },
      { name: 'audio_link', type: 'string', required: true },
      { name: 'is_voice', type: 'boolean', required: false, description: 'true for voice note' },
      { name: 'is_reply', type: 'boolean', required: false },
      { name: 'reply_wamid', type: 'string', required: false },
    ],
  },
  'send-template': {
    title: 'Send Template Message',
    method: 'POST',
    path: '/developer/message/send-template',
    auth: 'send',
    description: 'Send an approved WhatsApp template message. Marketing and Utility templates use variable parameters in header/body. AUTHENTICATION templates use Meta\'s predefined OTP format — pass the OTP code in body.parameters only; the Copy Code button component is added automatically when required. Wallet balance is checked per template category (marketing, utility, authentication).',
    body: [
      { name: 'number', type: 'string', required: true, description: 'Recipient WhatsApp number' },
      { name: 'template_id', type: 'string', required: true, description: 'Template ID from template list/details' },
      { name: 'component', type: 'array', required: true, description: 'WhatsApp send-time components with parameters (array or JSON string)' },
      { name: 'is_reply', type: 'boolean', required: false },
      { name: 'reply_wamid', type: 'string', required: false },
    ],
    exampleBodyObject: {
      number: '919876543210',
      template_id: 'your_template_id',
      component: [
        {
          type: 'body',
          parameters: [{ type: 'text', text: '123456' }],
        },
      ],
    },
    notes: [
      'AUTHENTICATION: component must include body.parameters[0].text with a 4–8 digit OTP code.',
      'AUTHENTICATION: omit the button component for COPY_CODE templates — the API adds it automatically.',
      'MARKETING / UTILITY: include header parameters when the template has a media or text header; include body parameters for {{1}}, {{2}}, etc.',
      'Use signed media URLs from template-details when filling header image/video/document parameters.',
    ],
    response: `{
  "message_id": "abc123...",
  "wamid": "wamid.HBgM...",
  "type": "out",
  "message_type": "template",
  "message": "123456 is your verification code. For your security, do not share this code.",
  "is_template": true,
  "status": "sent",
  "template": {
    "name": "login_otp",
    "category": "AUTHENTICATION",
    "language": "en",
    "components": [ /* template definition */ ]
  },
  "component": [
    { "type": "body", "parameters": [{ "type": "text", "text": "123456" }] },
    { "type": "button", "sub_type": "url", "index": "0", "parameters": [{ "type": "text", "text": "123456" }] }
  ],
  "send_by": { "username": "...", "name": "...", "mobile": "...", "email": "...", "status": true }
}`,
  },
  'template-list': {
    title: 'Template List',
    method: 'GET',
    path: '/developer/template/template-list',
    auth: 'template',
    description: 'List WhatsApp templates for your project. Template definitions are loaded from the database (template_json) with header media expanded to signed B2 URLs. Requires a Project Token.',
    headers: [{ name: 'token', required: true, description: 'Project Token' }],
    query: [
      { name: 'status', type: 'string', required: false, description: 'Filter by status (e.g. APPROVED). Partial match supported.' },
      { name: 'category', type: 'string', required: false, description: 'Filter by category: MARKETING, UTILITY, or AUTHENTICATION' },
      { name: 'page_no', type: 'number', required: false, description: 'Page number (default: 1)' },
      { name: 'limit', type: 'number', required: false, description: 'Items per page, max 100 (default: 20)' },
    ],
    response: `{
  "data": [
    {
      "template_id": "...",
      "waba_template_id": "...",
      "template_name": "login_otp",
      "language_code": "en",
      "category": "AUTHENTICATION",
      "status": "APPROVED",
      "create_date": "2026-06-20 14:30:00",
      "reject_reason": null,
      "template": {
        "name": "login_otp",
        "language": "en",
        "category": "AUTHENTICATION",
        "components": [
          { "type": "BODY", "add_security_recommendation": true },
          { "type": "FOOTER", "code_expiration_minutes": 10 },
          { "type": "BUTTONS", "buttons": [{ "type": "OTP", "otp_type": "COPY_CODE", "text": "Copy code" }] }
        ]
      }
    }
  ],
  "count": 10,
  "meta": { "page_no": 1, "limit": 20, "total_records": 10, "total_pages": 1, "has_more": false }
}`,
  },
  'template-details': {
    title: 'Template Details',
    method: 'GET',
    path: '/developer/template/template-details',
    auth: 'template',
    description: 'Get a single template by ID. Header image/video/document handles in template JSON are returned as signed B2 URLs ready for use in send-template header parameters.',
    query: [
      { name: 'template_id', type: 'string', required: true, description: 'Template ID' },
    ],
    response: `{
  "data": {
    "template_id": "...",
    "waba_template_id": "...",
    "template_name": "hello_world",
    "language_code": "en",
    "status": "APPROVED",
    "category": "MARKETING",
    "create_date": "2026-06-20 14:30:00",
    "project_id": "...",
    "reject_reason": null
  },
  "template": {
    "name": "hello_world",
    "category": "MARKETING",
    "language": "en",
    "components": [
      {
        "type": "HEADER",
        "format": "IMAGE",
        "example": { "header_handle": ["https://...signed-b2-url..."] }
      },
      { "type": "BODY", "text": "Hello {{1}}", "example": { "body_text": [["World"]] } }
    ]
  }
}`,
  },
  'create-template': {
    title: 'Create Template',
    method: 'POST',
    path: '/developer/template/create-template',
    auth: 'template',
    description: 'Create a new WhatsApp template and submit it to Meta for approval. Header media URLs in the request are downloaded and stored on B2; only filenames are saved in template_json. AUTHENTICATION templates must use Meta\'s predefined format (no custom body text). Requires a Project Token.',
    body: [
      { name: 'template', type: 'object', required: true, description: 'WhatsApp template JSON (name, language, category, components)' },
    ],
    exampleBodyObject: {
      template: {
        name: 'login_otp',
        language: 'en',
        category: 'AUTHENTICATION',
        components: [
          { type: 'BODY', add_security_recommendation: true },
          { type: 'FOOTER', code_expiration_minutes: 10 },
          {
            type: 'BUTTONS',
            buttons: [{ type: 'OTP', otp_type: 'COPY_CODE', text: 'Copy code' }],
          },
        ],
      },
    },
    notes: [
      'AUTHENTICATION: do not include custom body text or body variable samples — use add_security_recommendation and code_expiration_minutes only.',
      'MARKETING / UTILITY: header media can be a public URL; it will be uploaded to B2 during create.',
      'Status after create may be APPROVED, PENDING, or REJECTED depending on Meta review.',
    ],
    response: `{
  "template_id": "...",
  "template_name": "login_otp",
  "language_code": "en",
  "category": "AUTHENTICATION",
  "status": "PENDING",
  "template": { /* stored template with B2 media URLs expanded */ }
}`,
  },
  'edit-template': {
    title: 'Edit Template',
    method: 'POST',
    path: '/developer/template/edit-template',
    auth: 'template',
    description: 'Edit an existing template and resubmit to Meta. Media is re-processed to B2. AUTHENTICATION templates are validated against the predefined format. Template status becomes PENDING after edit.',
    body: [
      { name: 'template_id', type: 'string', required: true, description: 'Existing template ID' },
      { name: 'template', type: 'object', required: true, description: 'Updated template JSON' },
      { name: 'category', type: 'string', required: false, description: 'Override category if needed' },
    ],
    exampleBodyObject: {
      template_id: 'your_template_id',
      template: {
        name: 'login_otp',
        components: [
          { type: 'BODY', add_security_recommendation: true },
          { type: 'FOOTER', code_expiration_minutes: 15 },
          {
            type: 'BUTTONS',
            buttons: [{ type: 'OTP', otp_type: 'COPY_CODE', text: 'Copy code' }],
          },
        ],
      },
    },
    response: `{
  "template_id": "...",
  "template_name": "login_otp",
  "language_code": "en",
  "category": "AUTHENTICATION",
  "status": "PENDING",
  "template": { /* updated template with B2 media URLs expanded */ }
}`,
  },
  'bulk-upsert': {
    title: 'Bulk Upsert Contacts',
    method: 'POST',
    path: '/developer/contact/bulk-upsert',
    auth: 'send',
    description:
      'Create or update contacts in bulk for the authenticated project. Matching is done by phone number: existing numbers are updated, new numbers are inserted. The request is accepted immediately and processed in a background queue — use the returned job_id to poll status.',
    headers: [{ name: 'token', required: true, description: 'User Token or Project Token' }],
    body: [
      {
        name: 'number',
        type: 'string',
        required: true,
        description: 'WhatsApp number with country code (digits only preferred). Used as the upsert key.',
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'Contact display name. If omitted, the number is used as the name.',
      },
      { name: 'email', type: 'string', required: false, description: 'Contact email address' },
      { name: 'firm_name', type: 'string', required: false, description: 'Company / firm name' },
      { name: 'website', type: 'string', required: false, description: 'Website URL' },
      { name: 'remark', type: 'string', required: false, description: 'Internal note / remark' },
    ],
    exampleBodyObject: [
      {
        number: '91234567890',
        name: 'Test Contact',
        email: 'test@gmail.com',
        firm_name: 'Test Firm',
        website: 'https://www.test.com',
        remark: 'Test Remark',
      },
      {
        number: '91234567891',
        email: 'test1@gmail.com',
        firm_name: 'Test Firm 1',
        website: 'https://www.test1.com',
        remark: 'Test Remark 1',
      },
    ],
    notes: [
      'Send either a raw JSON array of contact objects, or { "contacts": [ ... ] }.',
      'Maximum 10,000 contacts per request.',
      'Processing runs in the background. The HTTP response returns as soon as the job is queued.',
      'New contacts are also assigned to the authenticated user in chat_assigned (same behaviour as create-contact).',
      'Poll GET /developer/contact/bulk-upsert-status?job_id=... for progress. Job status is retained for about 1 hour.',
    ],
    response: `{
  "error": false,
  "msg": "Bulk contact upsert accepted and is processing in the background",
  "job_id": "abc123xyz...",
  "total": 2,
  "status_url": "/developer/contact/bulk-upsert-status?job_id=abc123xyz..."
}`,
  },
  'bulk-upsert-status': {
    title: 'Bulk Upsert Status',
    method: 'GET',
    path: '/developer/contact/bulk-upsert-status',
    auth: 'send',
    description:
      'Check the status of a previously queued bulk contact upsert job. Only jobs belonging to the authenticated project can be queried.',
    headers: [{ name: 'token', required: true, description: 'User Token or Project Token' }],
    query: [
      { name: 'job_id', type: 'string', required: true, description: 'Job ID returned by bulk-upsert' },
    ],
    notes: [
      'status values: queued | processing | completed | failed.',
      'errors contains up to 100 sample failure/skip reasons (e.g. missing number).',
      'Jobs expire after about 1 hour and then return "Job not found or expired".',
    ],
    response: `{
  "error": false,
  "data": {
    "job_id": "abc123xyz...",
    "status": "completed",
    "total": 2,
    "processed": 2,
    "inserted": 1,
    "updated": 1,
    "skipped": 0,
    "failed": 0,
    "errors": []
  }
}`,
  },
  'access-info': {
    title: 'Access Info',
    method: 'POST',
    path: '/developer/access-info',
    auth: 'session',
    description: 'Returns developer access settings, project token, and user tokens for a project. Project owner only.',
    headers: [
      { name: 'token', type: 'string', required: true, description: 'Login session token' },
      { name: 'username', type: 'string', required: true, description: 'Logged-in username' },
    ],
    body: [
      { name: 'project_id', type: 'string', required: true, description: 'Project ID' },
    ],
    response: `{
  "error": false,
  "developer_access": true,
  "developer_token": "...",
  "users": [
    {
      "unique_id": "...",
      "username": "...",
      "name": "...",
      "email": "...",
      "type": "admin",
      "status": true,
      "developer_token": "..."
    }
  ]
}`,
  },
  'update-access': {
    title: 'Update Developer Access',
    method: 'POST',
    path: '/developer/update-developer-access',
    auth: 'session',
    description: 'Enable or disable developer API access for a project. Project owner only.',
    headers: [
      { name: 'token', type: 'string', required: true, description: 'Login session token' },
      { name: 'username', type: 'string', required: true, description: 'Logged-in username' },
    ],
    body: [
      { name: 'project_id', type: 'string', required: true, description: 'Project ID' },
      { name: 'status', type: 'boolean', required: true, description: 'true to enable, false to disable' },
    ],
    response: `{
  "error": false,
  "msg": "Developer access updated successfully",
  "developer_access": true
}`,
  },
  'project-token': {
    title: 'Generate Project Token',
    method: 'PUT',
    path: '/developer/update-developer-access',
    auth: 'session',
    description: 'Generate or regenerate the project-level developer token. Project owner only.',
    headers: [
      { name: 'token', type: 'string', required: true, description: 'Login session token' },
      { name: 'username', type: 'string', required: true, description: 'Logged-in username' },
    ],
    body: [
      { name: 'project_id', type: 'string', required: true, description: 'Project ID' },
    ],
    response: `{
  "error": false,
  "msg": "Developer token updated successfully",
  "developer_token": "..."
}`,
  },
  'user-token': {
    title: 'Generate User Token',
    method: 'PUT',
    path: '/developer/update-agent-developer-token',
    auth: 'session',
    description: 'Generate or regenerate a user developer token. Project owner only.',
    headers: [
      { name: 'token', type: 'string', required: true, description: 'Login session token' },
      { name: 'username', type: 'string', required: true, description: 'Logged-in username' },
    ],
    body: [
      { name: 'project_id', type: 'string', required: true, description: 'Project ID' },
      { name: 'unique_id', type: 'string', required: true, description: 'User mapping unique ID' },
    ],
    response: `{
  "error": false,
  "msg": "User developer token updated successfully",
  "developer_token": "..."
}`,
  },
};

export const authTypes = {
  session: {
    label: 'Session Auth',
    description: 'Used for Developer Access management from the dashboard. Send your login token and username in headers, and project_id in the JSON body.',
  },
  message: {
    label: 'User Token',
    description: 'Used to read conversations: chat list, chat history, and mark as read.',
  },
  send: {
    label: 'User or Project Token',
    description: 'Used to send messages and manage contacts (bulk upsert). A User Token acts as that user; a Project Token acts on behalf of the project.',
  },
  template: {
    label: 'Project Token',
    description: 'Required for template list, details, create, and edit endpoints.',
  },
};
